import { readFileSync } from 'fs';

import { ArrowBack as ArrowBackIcon, SendOutlined } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Container, Grid, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';


// Import layout
import ErrorBoundary from '../../components/common/ErrorBoundary';
import LayoutRouter from '../../components/layout/LayoutRouter';
// Import components
import AddressWarningDialog from '../../components/marketplace/AddressWarningDialog';
import QuoteSendModal from '../../components/marketplace/QuoteSendModal';
import RelatedServices from '../../components/marketplace/RelatedServices';
import ServiceUnavailableWarning from '../../components/marketplace/ServiceUnavailableWarning';
import ServiceJsonLd from '../../components/seo/ServiceJsonLd';
import BrandDisplay from '../../components/service/BrandDisplay';
import GuidedModePanel from '../../components/service/GuidedModePanel';
import OrderSchemaPanel from '../../components/service/OrderSchemaPanel';
import ServiceSections from '../../components/service/sections/ServiceSections';
import ServiceAddonSelector from '../../components/service/ServiceAddonSelector';
import ServiceBuyBox from '../../components/service/ServiceBuyBox';
import ServiceConfiguration from '../../components/service/ServiceConfiguration';
import ServiceHeader from '../../components/service/ServiceHeader';
import ServiceSpecs from '../../components/service/ServiceSpecs';
import ServiceVideoSection from '../../components/service/ServiceVideoSection';
import ServiceWhatToExpect from '../../components/service/ServiceWhatToExpect';
// Import hooks
import { WATERMARK_OPACITY } from '../../constants/service';
import { useAddress } from '../../contexts/AddressContext';
import { useAddressValidation } from '../../hooks/useAddressValidation';
import { useServiceData } from '../../hooks/useServiceData';
import { useServiceDetailAvailability } from '../../hooks/useServiceDetailAvailability';
import { useServicePricing } from '../../hooks/useServicePricing';
import { useTermSelection } from '../../hooks/useTermSelection';
// Import types
// Import constants
import { getAdditionalUserOffer } from '../../lib/services/additionalUsers';
import { getAvailableOneTimeAddons, getAvailableRecurringBoltOns } from '../../lib/services/configuredAddons';
import { GuidedModeRecommendation, isMultiLocationGuidedQuoteService } from '../../lib/services/guidedMode';
import type { RootState } from '../../store';
import { ServiceDetailPageProps } from '../../types/service';
import stripMarkdown from '../../utils/stripMarkdown';
import { validateServiceId } from '../../utils/validation';

// =============================================================================
// SSR HELPER FUNCTIONS - Internal API URL resolution
// =============================================================================
// These functions ensure SSR uses internal 127.0.0.1 addresses to bypass
// external routing through Apache reverse proxy.
// Reads ACTIVE_ENV from file (same as API does).

// FAANG-GRADE: Active environment file path (same as API)
const ACTIVE_ENV_FILE = '/home/aithonone/public_html/.active_env';

/**
 * Get the active environment (blue/green) from file
 */
function getActiveEnvironment(): string {
  try {
    const result = readFileSync(ACTIVE_ENV_FILE, 'utf8').trim();
    console.log('[SSR] Read .active_env file:', { path: ACTIVE_ENV_FILE, result });
    return result;
  } catch (error) {
    console.error('[SSR] Failed to read .active_env:', error);
    return 'unknown';
  }
}

/**
 * Get the API port based on deployment environment
 * Priority: Hostname detection (dev) > .active_env file (prod blue/green)
 */
function getApiPortFromHostname(hostname: string): string {
  const host = (hostname || '').toLowerCase();
  console.log('[SSR] getApiPortFromHostname called:', { hostname: hostname, host });

  // First: Check if this is dev by hostname (visionarynow.com)
  // This takes precedence - dev environments use hostname for detection
  if (host.includes('visionarynow.com')) {
    console.log('[SSR] Dev hostname detected, returning 3010');
    return '3010';
  }

  // Second: Check .active_env file for production blue/green
  const activeEnv = getActiveEnvironment();
  console.log('[SSR] Active environment:', activeEnv);

  if (activeEnv === 'green') {
    console.log('[SSR] Green env detected, returning 3030');
    return '3030';
  }
  if (activeEnv === 'blue') {
    console.log('[SSR] Blue env detected, returning 3020');
    return '3020';
  }

  // Fallback: Default to production blue (safest fallback)
  console.log('[SSR] Unknown env, falling back to 3020');
  return '3020';
}

/**
 * Get internal API URL using hostname-based port detection
 */
function getInternalApiUrl(path: string, hostname?: string): string {
  const port = hostname ? getApiPortFromHostname(hostname) : '3010';
  const url = `http://127.0.0.1:${port}${path}`;
  console.log('[SSR] getInternalApiUrl:', { path, hostname, port, url });
  return url;
}

// =============================================================================

// Import term selection hook

const truncateStr = (text: string, max: number) =>
  text.length <= max ? text : text.substring(0, max - 3) + '...';

function getSeoPriceData(service: any): {
  priceCents?: number;
  lowPriceCents?: number;
  highPriceCents?: number;
} {
  const explicitMin = Number(service?.price_range_min);
  const explicitMax = Number(service?.price_range_max);
  const explicitBase = Number(service?.base_price ?? service?.price);

  const variantRecurring: number[] = Array.isArray(service?.variants?.variants)
    ? service.variants.variants
        .map((v: any) => Number(v?.pricing?.recurring_cents))
        .filter((n: number) => Number.isFinite(n) && n > 0)
    : [];

  const derivedMin = variantRecurring.length > 0 ? Math.min(...variantRecurring) : undefined;
  const derivedMax = variantRecurring.length > 0 ? Math.max(...variantRecurring) : undefined;
  const defaultRecurring = Number(service?.default_pricing?.recurring_cents);
  const defaultNrc = Number(service?.default_pricing?.nrc_cents);

  const lowPriceCents =
    Number.isFinite(explicitMin) && explicitMin > 0
      ? explicitMin
      : derivedMin;

  const highPriceCents =
    Number.isFinite(explicitMax) && explicitMax > 0
      ? explicitMax
      : derivedMax;

  const hasValidRange =
    lowPriceCents != null &&
    highPriceCents != null &&
    highPriceCents >= lowPriceCents &&
    lowPriceCents !== highPriceCents;

  const fallbackSingle =
    Number.isFinite(defaultRecurring) && defaultRecurring > 0
      ? defaultRecurring
      : Number.isFinite(defaultNrc) && defaultNrc > 0
        ? defaultNrc
        : Number.isFinite(explicitBase) && explicitBase > 0
          ? explicitBase
          : lowPriceCents;

  return {
    priceCents: hasValidRange ? undefined : fallbackSingle,
    lowPriceCents: hasValidRange ? lowPriceCents : undefined,
    highPriceCents: hasValidRange ? highPriceCents : undefined,
  };
}

const ServiceDetailPage: React.FC<ServiceDetailPageProps> = ({
  serviceId,
  serviceData: initialServiceData,
  buyBoxData,
  error: serverError,
  serviceUnavailable,
}) => {
  const router = useRouter();
  const { selectedAddress } = useAddress();
  const [selectedVariant, setSelectedVariant] = useState<string | undefined>();
  const [selectedFrequency, setSelectedFrequency] = useState<string | undefined>();
  const [selectedAddons, setSelectedAddons] = useState<Record<string, string>>({});
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [guidedRecommendation, setGuidedRecommendation] = useState<GuidedModeRecommendation | null>(null);

  // Auth state via Redux (same pattern as AddToCartButton, dashboards)
  const isAuthenticated = !!useSelector((state: RootState) => state.auth.token);

  // Use service data hook with term support
  const {
    service: serviceData,
    loading,
    error: clientError,
    refetch,
  } = useServiceData(
    serviceId,
    initialServiceData,
    initialServiceData?.default_term_months || undefined, // Start with default term
  );

  // Use term selection hook
  const { selectedTerm, handleTermChange } = useTermSelection(serviceData);

  // CRITICAL: All hooks MUST be called unconditionally before any early returns.
  // React error #300 ("Rendered fewer hooks than expected") occurs if hooks are
  // called after a conditional return, because React sees fewer hooks on re-render.
  const { addressWarningDialog, handleAddressWarningConfirm, handleAddressWarningCancel } =
    useAddressValidation(serviceData || null, router);
  const { currentServiceUnavailable } = useServiceDetailAvailability(
    serviceData,
    selectedAddress?.id,
    serviceId,
  );
  const { selectedVariantPrice } = useServicePricing(
    serviceData,
    selectedVariant,
    selectedFrequency,
    selectedTerm,
  );

  // Auto-select default variant if available
  useEffect(() => {
    if (serviceData && !selectedVariant && serviceData.variants?.variants) {
      const defaultVariant = serviceData.variants.variants.find((v: any) => v.is_default); // variants-access-ok (gated by `serviceData.variants?.variants` above)
      if (defaultVariant) {
        setSelectedVariant(defaultVariant.key);
      }
    }
  }, [serviceData, selectedVariant]);

  // Create theme
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: '#1976d2' },
          secondary: { main: '#dc004e' },
        },
      }),
    [],
  );

  // Refetch service data when term changes
  const handleTermChangeWithRefetch = React.useCallback((term: number) => {
    handleTermChange(term);
    setSelectedVariant(undefined); // Reset variant selection when term changes
    refetch(term); // Refetch with new term for term-specific pricing
  }, [handleTermChange, refetch]);

  // --- All hooks called above this line. Early returns below are safe. ---

  // Loading state
  if (loading) {
    return (
      <LayoutRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading service details...</Typography>
            </Box>
          </Container>
        </ThemeProvider>
      </LayoutRouter>
    );
  }

  // Service unavailable state (from server-side or client-side check)
  if (serviceUnavailable || currentServiceUnavailable) {
    const unavailableData = serviceUnavailable || currentServiceUnavailable;
    return (
      <LayoutRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ServiceUnavailableWarning
            service={unavailableData!.service}
            restrictions={unavailableData!.restrictions}
            checkedAddress={unavailableData!.checkedAddress}
            onChangeAddress={() => {
              router.push('/profile?tab=addresses');
            }}
            onReturnToMarketplace={() => {
              router.push('/');
            }}
          />
        </ThemeProvider>
      </LayoutRouter>
    );
  }

  // Error state
  if (serverError || clientError || !serviceData) {
    return (
      <LayoutRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverError || clientError || 'Service not found'}
            </Alert>
            <Button startIcon={<ArrowBackIcon />} onClick={() => router.back()} variant="outlined">
              Go Back
            </Button>
          </Container>
        </ThemeProvider>
      </LayoutRouter>
    );
  }

  const service = serviceData;
  const isMultiLocationGuidedQuote = isMultiLocationGuidedQuoteService(service as any);
  const addons = ((service.metadata as any)?.addons || []) as any[];
  const variantKey = selectedVariant || service.default_variant_key;
  const hasConfiguredAddons =
    getAdditionalUserOffer(service as any, variantKey).enabled ||
    getAvailableRecurringBoltOns(service as any, variantKey).length > 0 ||
    getAvailableOneTimeAddons(service as any, variantKey).length > 0;

  const handleAddonChange = (addonId: string, value: string) => {
    setSelectedAddons((prev) => ({ ...prev, [addonId]: value }));
  };

  const addonPriceCents = Object.entries(selectedAddons).reduce((total, [addonId, value]) => {
    const addon = addons.find((a: any) => a.id === addonId);
    const option = addon?.options?.find((o: any) => o.value === value);
    return total + (option?.price_cents || 0);
  }, 0);

  const selectedAddonLabel = Object.entries(selectedAddons).reduce((label, [addonId, value]) => {
    if (label) return label;
    const addon = addons.find((a: any) => a.id === addonId);
    const option = addon?.options?.find((o: any) => o.value === value);
    return option?.price_cents > 0 ? option.label : '';
  }, '');

  const seoDescription = stripMarkdown(service.description || '');
  const seoPriceData = getSeoPriceData(service);

  return (
    <ErrorBoundary>
    <LayoutRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        {/* Full-screen watermark background */}
        {service.brand_logo_path && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundImage: `url(${service.brand_logo_path})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              opacity: WATERMARK_OPACITY,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}

        {/* SSR breadcrumb + summary — visible to crawlers, styled as breadcrumb */}
        {serviceData && (
          <Box component="nav" aria-label="breadcrumb" sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", py: 1, px: { xs: 2, md: 4 } }}>
            <Box sx={{ maxWidth: 1600, mx: "auto", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
              <Box component="a" href="/" sx={{ fontSize: "0.75rem", color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>Marketplace</Box>
              {(serviceData as any).category && <>
                <Typography variant="caption" color="text.disabled">/</Typography>
                <Box component="a" href={"/?category=" + encodeURIComponent((serviceData as any).category)} sx={{ fontSize: "0.75rem", color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>{(serviceData as any).category}</Box>
              </>}
              <Typography variant="caption" color="text.disabled">/</Typography>
              <Typography variant="caption" color="text.primary" fontWeight={600}>{serviceData.name}</Typography>
              {(serviceData as any).brand_name && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>by {(serviceData as any).brand_name}</Typography>
              )}
            </Box>
          </Box>
        )}

        <Head>
          <title>{service.name} - Service Details</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href={`https://aithon.tech/services/${serviceId}`} />
          <meta property="og:type" content="product" />
          <meta property="og:title" content={`${service.name} — Aithon`} />
          <meta property="og:description" content={truncateStr(seoDescription, 200)} />
          <meta property="og:url" content={`https://aithon.tech/services/${serviceId}`} />
          <meta property="og:image" content={(service as any).image || 'https://aithon.tech/og-default.png'} />
          <meta property="og:site_name" content="Aithon" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${service.name} — Aithon`} />
          <meta name="twitter:description" content={truncateStr(seoDescription, 200)} />
          <meta name="twitter:image" content={(service as any).image || 'https://aithon.tech/og-default.png'} />
        </Head>
        <ServiceJsonLd
          name={service.name}
          description={seoDescription}
          url={`https://aithon.tech/services/${serviceId}`}
          priceCents={seoPriceData.priceCents}
          lowPriceCents={seoPriceData.lowPriceCents}
          highPriceCents={seoPriceData.highPriceCents}
          offerCount={(service as any).variants?.variants?.length || undefined}
          category={service.category}
          brandName={(service as any).brand_name || undefined}
          providerName={(service as any).brand_name || 'Aithon'}
          pricingModel={(service as any).pricing_model || undefined}
        />

        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Back Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => router.push('/')}
              variant="outlined"
            >
              Back to Marketplace
            </Button>
          </Box>

          {/* Main Content Grid */}
          <Grid container spacing={4}>
            {/* Left Column - Main Content (2/3 width) */}
            <Grid item xs={12} md={8}>
              <ServiceHeader service={service} />

              {/* Key specs strip — auto-derived from variants + category */}
              <ServiceSpecs service={service} />

              {isMultiLocationGuidedQuote ? (
                <GuidedModePanel service={service as any} onRecommendationChange={setGuidedRecommendation} />
              ) : (
                <ServiceConfiguration
                  service={service}
                  selectedVariant={selectedVariant}
                  selectedFrequency={selectedFrequency}
                  onVariantChange={setSelectedVariant}
                  onFrequencyChange={setSelectedFrequency}
                  selectedTerm={selectedTerm}
                  onTermChange={handleTermChangeWithRefetch}
                />
              )}

              <OrderSchemaPanel service={service} />

              {addons.length > 0 && !hasConfiguredAddons && (
                <ServiceAddonSelector
                  addons={addons}
                  selectedAddons={selectedAddons}
                  onChange={handleAddonChange}
                />
              )}

              {/* What to expect — auto-selected by service category */}
              <ServiceWhatToExpect service={service} />

              {/* Structured sections from metadata */}
              <ServiceSections service={service} />

            </Grid>

            {/* Right Column - Buy Box (1/3 width) */}
            <ServiceBuyBox
              service={service}
              selectedVariant={selectedVariant}
              selectedVariantPrice={selectedVariantPrice}
              selectedTerm={selectedTerm}
              selectedFrequency={selectedFrequency}
              addonPriceCents={addonPriceCents}
              addonLabel={selectedAddonLabel}
              guidedRecommendation={guidedRecommendation}
              showGuidedPanel={!isMultiLocationGuidedQuote}
            />

            {/* Service-specific video — full width, above About the company */}
            {(service as any).service_video_url && (
              <Grid item xs={12}>
                <ServiceVideoSection
                  videoUrl={(service as any).service_video_url}
                  serviceName={service.name}
                />
              </Grid>
            )}

            {/* Provider Information — full width below both columns */}
            <Grid item xs={12} sx={{ mt: 0 }}>
              <BrandDisplay
                brandName={service.brand_name}
                brandDescription={service.brand_description}
                brandLogoPath={service.brand_logo_path}
                brandVideoUrl={service.brand_video_url}
                serviceVideoUrl={service.service_video_url}
                brandWebsite={service.brand_website}
                installDays={service.expected_install_time_days}
              />
            </Grid>

            {/* Send Quote — partners only */}
            {isAuthenticated && service && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SendOutlined />}
                  onClick={() => setQuoteModalOpen(true)}
                  sx={{ mt: 1, ml: 2 }}
                >
                  Send Quote
                </Button>
                <QuoteSendModal
                  open={quoteModalOpen}
                  onClose={() => setQuoteModalOpen(false)}
                  services={[{ id: service.id, name: service.name }]}
                />
              </>
            )}
          </Grid>

          <AddressWarningDialog
            open={addressWarningDialog.open}
            onClose={handleAddressWarningCancel}
            onConfirm={handleAddressWarningConfirm}
            onCancel={handleAddressWarningCancel}
            serviceName={service.name}
          />

          {/* Customers Also Viewed */}
          <RelatedServices serviceId={serviceId} />
        </Container>
      </ThemeProvider>
    </LayoutRouter>
    </ErrorBoundary>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id } = context.params || {};
  const validatedId = validateServiceId(id);
  const hostname = context.req.headers.host || 'localhost:3011';

  console.log('[SSR] ============================================');
  console.log('[SSR] ServiceDetailPage SSR Start');
  console.log('[SSR] id:', id);
  console.log('[SSR] validatedId:', validatedId);
  console.log('[SSR] hostname:', hostname);
  console.log('[SSR] ============================================');

  if (!validatedId) {
    console.log('[SSR] Invalid service ID, returning 404');
    return {
      notFound: true,
    };
  }

  try {
    // Use internal API URL based on hostname (dev vs prod detection)
    const serviceApiUrl = getInternalApiUrl(`/api/catalog/services/${validatedId}`, hostname);
    console.log('[SSR] Fetching service from:', serviceApiUrl);

    const serviceResponse = await fetch(serviceApiUrl);
    console.log('[SSR] Service API response status:', serviceResponse.status);

    if (!serviceResponse.ok) {
      if (serviceResponse.status === 404) {
        console.log('[SSR] Service not found (404), returning 404');
        return {
          notFound: true,
        };
      }
      const errorText = await serviceResponse.text();
      console.log('[SSR] Service API error:', errorText);
      throw new Error(`Failed to fetch service: ${serviceResponse.status}`);
    }

    let serviceData = await serviceResponse.json();
    console.log('[SSR] Service data fetched successfully');

    // If service has a default term, refetch with it for proper SSR pricing
    const service = serviceData.service || serviceData;
    if (service.default_term_months) {
      try {
        const termUrl = getInternalApiUrl(
          `/api/catalog/services/${validatedId}?term=${service.default_term_months}`,
          hostname,
        );
        const termResponse = await fetch(termUrl);
        if (termResponse.ok) {
          serviceData = await termResponse.json();
          console.log('[SSR] Refetched with default term:', service.default_term_months);
        }
      } catch {
        // non-fatal — fall through with base service data
      }
    }

    // Fetch buy box data (similar to BuyBoxTool)
    let buyBoxData = null;
    try {
      const buyBoxApiUrl = getInternalApiUrl(`/api/marketplace/services/${validatedId}`, hostname);
      console.log('[SSR] Fetching buy box from:', buyBoxApiUrl);

      const buyBoxResponse = await fetch(buyBoxApiUrl);
      console.log('[SSR] BuyBox API response status:', buyBoxResponse.status);

      if (buyBoxResponse.ok) {
        const detailedService = await buyBoxResponse.json();
        console.log('[SSR] BuyBox data fetched successfully');

        // Use the already-fetched detailed service for buy box data
        // No need for a separate catalog listing call — the service detail
        // endpoint already returns everything needed for the buy box
        buyBoxData = {
          buyBoxWinner: detailedService.buyBoxWinner || null,
          perks: detailedService.perks || [],
          detailedService,
        };
        console.log('[SSR] BuyBox data assembled from service detail');
      } else {
        console.log('[SSR] BuyBox response not OK, skipping buyBox');
      }
    } catch (buyBoxError) {
      console.log('[SSR] BuyBox fetch error:', buyBoxError);
      // Continue without buy box data
    }

    console.log('[SSR] ============================================');
    console.log('[SSR] ServiceDetailPage SSR Complete - Returning props');
    console.log('[SSR] ============================================');

    const baseService = serviceData.service || serviceData;
    const detailedExtra = (buyBoxData?.detailedService) || {};
    return {
      props: {
        serviceId: validatedId,
        serviceData: {
          ...baseService,
          brand_video_url: (baseService as any).brand_video_url ?? (detailedExtra as any).brand_video_url ?? null,
          service_video_url: (baseService as any).service_video_url ?? (detailedExtra as any).service_video_url ?? null,
          brand_website: (baseService as any).brand_website ?? (detailedExtra as any).brand_website ?? null,
          order_schema: (baseService as any).order_schema ?? (detailedExtra as any).order_schema ?? null,
        },
        buyBoxData,
      },
    };
  } catch (error) {
    console.error('[SSR] Error fetching service:', error);
    return {
      props: {
        serviceId: validatedId,
        error: 'Failed to load service details',
      },
    };
  }
};

export default React.memo(ServiceDetailPage);
